import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  BookOpen,
  Calendar,
  CheckCircle,
  AlertCircle,
  TrendingUp,
  BarChart3,
  Layers,
  Plus,
  Play,
  X,
  ChevronRight,
  FileText,
} from "lucide-react";
import AdminAPI from "../../services/AdminAPI";

const ACPP_SETTINGS_KEY = "acpp_portal_settings";

function fmtDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
}

function statusMeta(plan) {
  const pct = plan.total_modules > 0
    ? Math.round((plan.modules_filled / plan.total_modules) * 100)
    : 0;
  if (pct >= 100) return { label: "Completed", color: "bg-green-100 text-green-700", dot: "bg-green-500" };
  if (pct >= 60) return { label: "In Progress", color: "bg-blue-100 text-blue-700", dot: "bg-blue-500" };
  if (pct > 0) return { label: "Partially Filled", color: "bg-orange-100 text-orange-700", dot: "bg-orange-500" };
  return { label: "Not Started", color: "bg-yellow-100 text-yellow-700", dot: "bg-yellow-400" };
}

function buildRingStyle(percent) {
  const deg = Math.round((percent / 100) * 360);
  return `conic-gradient(#2d81c1 0deg ${deg}deg, #edf2f7 ${deg}deg 360deg)`;
}

function setActiveACPPYear(year) {
  localStorage.setItem(ACPP_SETTINGS_KEY, JSON.stringify({ academicYear: year }));
}

function NewPlanModal({ onClose, onCreate }) {
  const [year, setYear] = useState("");
  const [label, setLabel] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleCreate = async () => {
    if (!/^\d{4}-\d{2,4}$/.test(year.trim())) {
      setError("Enter a valid academic year (e.g. 2027-28)");
      return;
    }
    setError("");
    setLoading(true);
    await onCreate(year.trim(), label.trim() || null);
    setLoading(false);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6 font-[poppins]">
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: "#D67C2C" }}>
              <Plus className="w-5 h-5 text-white" />
            </div>
            <h2 className="text-lg font-semibold text-gray-900">New ACPP Plan</h2>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              Academic Year <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              placeholder="e.g. 2027-28"
              value={year}
              onChange={(e) => {
                setYear(e.target.value);
                setError("");
              }}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:border-transparent"
              style={{ "--tw-ring-color": "#D67C2C" }}
            />
            {error && <p className="text-xs text-red-500 mt-1">{error}</p>}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              Plan Label <span className="text-gray-400">(optional)</span>
            </label>
            <input
              type="text"
              placeholder={year ? `${year} ACPP Plan` : "e.g. 2027-28 ACPP Plan"}
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:border-transparent"
            />
          </div>
          <div className="bg-orange-50 border border-orange-100 rounded-lg p-3 text-xs text-orange-700">
            Creating a new plan will switch the active ACPP year to <strong>{year || "…"}</strong>. Existing ACPP plan data is preserved and can be resumed anytime.
          </div>
        </div>

        <div className="flex gap-3 mt-6">
          <button
            onClick={onClose}
            className="flex-1 py-2.5 border border-gray-300 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleCreate}
            disabled={loading || !year.trim()}
            className="flex-1 py-2.5 text-white rounded-lg text-sm font-medium disabled:opacity-50 transition-colors flex items-center justify-center gap-2"
            style={{ backgroundColor: loading || !year.trim() ? "#e0a070" : "#D67C2C" }}
          >
            {loading ? (
              <>
                <span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                Creating…
              </>
            ) : (
              <>
                <Plus className="w-4 h-4" />
                Create & Switch
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function ACPPPlansManager() {
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [activeYear, setActiveYear] = useState("");
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [switchingYear, setSwitchingYear] = useState(null);
  const [toast, setToast] = useState(null);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState("");
  const [yearFilter, setYearFilter] = useState("");

  useEffect(() => {
    AdminAPI.acppGetPlans()
      .then((res) => {
        if (res?.plans) {
          setPlans(res.plans);
          setActiveYear(res.active_year || "");
        }
      })
      .catch(() => setError("Failed to load ACPP plans. Please try again."))
      .finally(() => setLoading(false));
  }, []);

  const showToast = (msg, type = "success") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  };

  const handleSwitch = async (year) => {
    setSwitchingYear(year);
    try {
      await AdminAPI.acppActivatePlan(year);
      setActiveACPPYear(year);
      setActiveYear(year);
      setPlans((prev) => prev.map((p) => ({ ...p, is_active: p.academic_year === year })));
      showToast(`Switched to ${year}`);
    } catch {
      showToast("Failed to switch year", "error");
    } finally {
      setSwitchingYear(null);
    }
  };

  const handleOpen = async (year) => {
    if (year !== activeYear) {
      await handleSwitch(year);
    }
    navigate("/ACPP");
  };

  const handleCreate = async (year, label) => {
    try {
      const res = await AdminAPI.acppCreatePlan(year, label, true);
      setActiveACPPYear(year);
      setActiveYear(year);
      const newPlan = {
        academic_year: res.academic_year,
        label: res.label,
        created_at: res.created_at,
        last_updated_at: null,
        modules_filled: 0,
        total_modules: 12,
        is_active: true,
      };
      setPlans((prev) => [newPlan, ...prev.map((p) => ({ ...p, is_active: false }))]);
      setShowModal(false);
      showToast(`${year} ACPP plan created — ready to start!`);
      navigate("/ACPP");
    } catch {
      showToast("Failed to create plan", "error");
    }
  };

  const totalPlans = plans.length;
  const activePlan = plans.find((p) => p.is_active);
  const activePct = activePlan
    ? Math.round((activePlan.modules_filled / activePlan.total_modules) * 100)
    : 0;
  const completedCount = plans.filter((p) =>
    Math.round((p.modules_filled / p.total_modules) * 100) >= 100
  ).length;
  const inProgressCount = plans.filter((p) => {
    const pct = Math.round((p.modules_filled / p.total_modules) * 100);
    return pct > 0 && pct < 100;
  }).length;

  const statusOptions = ["Completed", "In Progress", "Partially Filled", "Not Started"];
  const yearOptions = Array.from(new Set(plans.map((p) => p.academic_year).filter(Boolean))).sort();

  const filteredPlans = plans.filter((plan) => {
    const meta = statusMeta(plan);
    if (statusFilter && meta.label !== statusFilter) return false;
    if (yearFilter && plan.academic_year !== yearFilter) return false;
    return true;
  });

  return (
    <>
      <div className="bg-white p-8 min-h-screen rounded-b-2xl rounded-tr-2xl shadow-xl font-[poppins]">
        <div className="w-full px-4 sm:px-6 lg:px-8 py-8">
          {error && (
            <div className="fixed top-4 right-4 z-50 p-4 rounded-lg shadow-lg flex items-center gap-3 bg-red-500 text-white" style={{ animation: "slideInRight 0.5s ease-out" }}>
              <AlertCircle className="w-5 h-5 flex-shrink-0" />
              <span>{error}</span>
              <button onClick={() => setError(null)} className="ml-2 hover:opacity-80"><X className="w-4 h-4" /></button>
            </div>
          )}

          <div className="pt-8 pb-8 text-center relative overflow-hidden">
            <div className="relative z-10">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full mb-6" style={{ backgroundColor: "#D67C2C" }}>
                <BookOpen className="w-8 h-8 text-white" />
              </div>
              <h1 className="text-5xl font-bold bg-clip-text text-transparent mb-4" style={{ backgroundImage: "linear-gradient(135deg, #D67C2C, #c06a25)" }}>
                ACPP Plans
              </h1>
              <p className="text-[#1D3469] text-xl font-medium max-w-3xl mx-auto px-4">
                Manage, track and switch between academic year ACPP plans
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-10">
            <div className="bg-white rounded-xl shadow-md overflow-hidden border border-gray-200 hover:shadow-lg transition-all duration-300">
              <div className="p-6 flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-500">Total Plans</p>
                  <p className="text-3xl font-bold text-blue-600 mt-1">{totalPlans}</p>
                </div>
                <div className="p-3 rounded-full bg-blue-100">
                  <Layers className="w-6 h-6 text-blue-600" />
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-md overflow-hidden border border-gray-200 hover:shadow-lg transition-all duration-300">
              <div className="p-6 flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-500">Active Plan Progress</p>
                  <p className="text-3xl font-bold mt-1" style={{ color: "#D67C2C" }}>{activePct}%</p>
                </div>
                <div className="p-3 rounded-full" style={{ backgroundColor: "#FEF0E7" }}>
                  <TrendingUp className="w-6 h-6" style={{ color: "#D67C2C" }} />
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-md overflow-hidden border border-gray-200 hover:shadow-lg transition-all duration-300">
              <div className="p-6 flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-500">Completed Plans</p>
                  <p className="text-3xl font-bold text-green-600 mt-1">{completedCount}</p>
                </div>
                <div className="p-3 rounded-full bg-green-100">
                  <CheckCircle className="w-6 h-6 text-green-600" />
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-md overflow-hidden border border-gray-200 hover:shadow-lg transition-all duration-300">
              <div className="p-6 flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-500">In Progress</p>
                  <p className="text-3xl font-bold text-purple-600 mt-1">{inProgressCount}</p>
                </div>
                <div className="p-3 rounded-full bg-purple-100">
                  <BarChart3 className="w-6 h-6 text-purple-600" />
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-md overflow-hidden border border-gray-200">
            <div className="p-6 border-b border-gray-100 bg-gray-50">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: "#D67C2C" }}>
                    <FileText className="w-5 h-5 text-white" />
                  </div>
                  <h2 className="text-xl font-semibold text-gray-900 font-[poppins]">All ACPP Plans</h2>
                </div>
                <button
                  onClick={() => setShowModal(true)}
                  className="inline-flex items-center gap-2 px-4 py-2 text-white text-sm font-medium rounded-xl transition-colors shadow-sm hover:opacity-90"
                  style={{ backgroundColor: "#D67C2C" }}
                >
                  <Plus className="w-4 h-4" />
                  New Plan
                </button>
              </div>
            </div>

            {!loading && plans.length > 0 && (
              <div className="p-6 border-b border-gray-100 bg-white">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
                    <select
                      value={statusFilter}
                      onChange={(e) => setStatusFilter(e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:border-transparent"
                      style={{ "--tw-ring-color": "#D67C2C" }}
                    >
                      <option value="">All Status</option>
                      {statusOptions.map((s) => (
                        <option key={s} value={s}>{s}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Academic Year</label>
                    <select
                      value={yearFilter}
                      onChange={(e) => setYearFilter(e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:border-transparent"
                      style={{ "--tw-ring-color": "#D67C2C" }}
                    >
                      <option value="">All Years</option>
                      {yearOptions.map((y) => (
                        <option key={y} value={y}>{y}</option>
                      ))}
                    </select>
                  </div>
                  {(statusFilter || yearFilter) && (
                    <div className="flex items-end">
                      <button
                        onClick={() => { setStatusFilter(""); setYearFilter(""); }}
                        className="w-full px-4 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm hover:bg-gray-200 transition-colors"
                      >
                        Clear Filters
                      </button>
                    </div>
                  )}
                </div>
              </div>
            )}

            {loading ? (
              <div className="text-center py-16">
                <div className="w-10 h-10 border-4 border-gray-200 rounded-full animate-spin mx-auto mb-4" style={{ borderTopColor: "#D67C2C" }} />
                <p className="text-gray-500 text-sm">Loading plans…</p>
              </div>
            ) : plans.length === 0 ? (
              <div className="text-center py-16">
                <BookOpen className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                <p className="text-lg font-medium text-gray-500 mb-1">No ACPP plans yet</p>
                <p className="text-sm text-gray-400 mb-6">Create your first ACPP plan to get started</p>
                <button
                  onClick={() => setShowModal(true)}
                  className="inline-flex items-center gap-2 px-6 py-2.5 text-white text-sm font-medium rounded-xl hover:opacity-90 transition-colors"
                  style={{ backgroundColor: "#D67C2C" }}
                >
                  <Plus className="w-4 h-4" />
                  Create First Plan
                </button>
              </div>
            ) : filteredPlans.length === 0 ? (
              <div className="text-center py-16">
                <BookOpen className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                <p className="text-lg font-medium text-gray-500 mb-1">No plans match your filters</p>
                <p className="text-sm text-gray-400">Try adjusting your Status or Academic Year filter</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Academic Year</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Progress</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Created</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Last Updated</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredPlans.map((plan) => {
                      const meta = statusMeta(plan);
                      const pct = plan.total_modules > 0
                        ? Math.round((plan.modules_filled / plan.total_modules) * 100)
                        : 0;
                      const isSwitching = switchingYear === plan.academic_year;

                      return (
                        <tr key={plan.academic_year} className="hover:bg-gray-50 transition-colors">
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-3">
                              <div
                                className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0"
                                style={{ background: buildRingStyle(pct) }}
                              >
                                <div className="w-6 h-6 bg-white rounded-full flex items-center justify-center">
                                  <Calendar className="w-3 h-3 text-gray-500" />
                                </div>
                              </div>
                              <div>
                                <div className="flex items-center gap-2">
                                  <span className="text-sm font-semibold text-gray-900">{plan.academic_year}</span>
                                  {plan.is_active && (
                                    <span className="text-[10px] px-1.5 py-0.5 rounded font-semibold text-white" style={{ backgroundColor: "#D67C2C" }}>Active</span>
                                  )}
                                </div>
                                <div className="text-xs text-gray-500">{plan.label}</div>
                              </div>
                            </div>
                          </td>

                          <td className="px-6 py-4">
                            <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${meta.color}`}>
                              <span className={`w-1.5 h-1.5 rounded-full ${meta.dot}`} />
                              {meta.label}
                            </span>
                          </td>

                          <td className="px-6 py-4">
                            <div className="flex items-center gap-2 min-w-[120px]">
                              <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
                                <div
                                  className="h-full rounded-full transition-all"
                                  style={{
                                    width: `${pct}%`,
                                    backgroundColor: pct >= 100 ? "#22c55e" : pct >= 60 ? "#3b82f6" : pct > 0 ? "#f97316" : "#facc15",
                                  }}
                                />
                              </div>
                              <span className="text-xs text-gray-600 whitespace-nowrap">
                                {plan.modules_filled}/{plan.total_modules}
                              </span>
                            </div>
                          </td>

                          <td className="px-6 py-4 text-sm text-gray-500">{fmtDate(plan.created_at)}</td>
                          <td className="px-6 py-4 text-sm text-gray-500">{fmtDate(plan.last_updated_at)}</td>

                          <td className="px-6 py-4">
                            {plan.is_active ? (
                              <button
                                onClick={() => handleOpen(plan.academic_year)}
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-white rounded-lg hover:opacity-90 transition-colors"
                                style={{ backgroundColor: "#D67C2C" }}
                              >
                                <Play className="w-3.5 h-3.5" />
                                Continue
                              </button>
                            ) : (
                              <div className="flex items-center gap-2">
                                <button
                                  onClick={() => handleSwitch(plan.academic_year)}
                                  disabled={isSwitching}
                                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
                                >
                                  {isSwitching ? (
                                    <span className="w-3.5 h-3.5 border-2 border-gray-400 border-t-gray-700 rounded-full animate-spin" />
                                  ) : (
                                    <ChevronRight className="w-3.5 h-3.5" />
                                  )}
                                  {isSwitching ? "Switching…" : "Switch"}
                                </button>
                                <button
                                  onClick={() => handleOpen(plan.academic_year)}
                                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-lg transition-colors"
                                >
                                  Open
                                </button>
                              </div>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        {showModal && (
          <NewPlanModal
            onClose={() => setShowModal(false)}
            onCreate={handleCreate}
          />
        )}

        {toast && (
          <div
            className={`fixed bottom-6 right-6 z-50 flex items-center gap-3 px-4 py-3 rounded-xl shadow-lg text-sm font-medium text-white transition-all ${toast.type === "error" ? "bg-red-500" : "bg-green-600"}`}
            style={{ animation: "slideInRight 0.3s ease-out" }}
          >
            {toast.type === "error" ? <AlertCircle className="w-4 h-4" /> : <CheckCircle className="w-4 h-4" />}
            {toast.msg}
          </div>
        )}
      </div>
    </>
  );
}
